// ============================================================================
// Execution Strategy — HTML RENDERER
// ============================================================================

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const arr = (v) => Array.isArray(v) ? v.filter(Boolean) : [];
const badgeTone = (value = '') => {
  const v = String(value).toLowerCase();
  if (v.includes('critical') || v.includes('very high')) return 'danger';
  if (v.includes('high')) return 'warning';
  if (v.includes('medium')) return 'info';
  if (v.includes('low')) return 'neutral';
  if (v.includes('0-30')) return 'danger';
  if (v.includes('30-60')) return 'warning';
  if (v.includes('60-90')) return 'info';
  return 'neutral';
};

const badge = (text, tone = 'neutral') => `<span class="aes-badge ${tone}">${esc(text)}</span>`;

const renderDocHeader = (modelName) => {
  const timestamp = new Date().toLocaleString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric', 
    hour: '2-digit', minute: '2-digit', hour12: true 
  });
  return `
<div class="aes-doc-header">
  <div class="aes-doc-header-left">
    <div class="aes-doc-header-type">AI Execution Strategy</div>
    <div class="aes-doc-header-title">${esc(modelName || 'Process Model')}</div>
  </div>
  <div class="aes-doc-header-time">${timestamp}</div>
</div>`;
};

const renderList = (items = [], cls = 'aes-list-item') =>
  arr(items).map((item) => `<div class="${cls}">${esc(item)}</div>`).join('');

const priorityCard = (item = {}) => {
  const deps = renderList(item.dependencies, 'aes-subitem');
  const risks = renderList(item.risks, 'aes-subitem');
  const measures = renderList(item.success_measures, 'aes-subitem');
  const evidence = renderList(item.evidence, 'aes-evidence-item');

  return `
  <div class="aes-card">
    <div class="aes-card-bar">
      <div class="aes-rank-block">
        <div class="aes-rank-label">Priority</div>
        <div class="aes-rank-value">${esc(item.rank ?? '')}</div>
      </div>
      <div class="aes-card-title-block">
        <div class="aes-card-title">${esc(item.title)}</div>
        ${item.source_opportunity ? `<div class="aes-source-link">From Opportunity Portfolio: ${esc(item.source_opportunity)}</div>` : ''}
        <div class="aes-card-badges">
          ${item.recommended_priority ? badge(item.recommended_priority, item.recommended_priority === 'Do Now' ? 'info' : 'neutral') : ''}
          ${item.value_potential ? badge(`Value: ${item.value_potential}`, badgeTone(item.value_potential)) : ''}
          ${Number.isFinite(Number(item.priority_score)) && Number(item.priority_score) > 0 ? badge(`Score ${item.priority_score}/100`, 'neutral') : ''}
          ${item.opportunity_type ? badge(item.opportunity_type, 'neutral') : ''}
          ${badge(item.action_type, 'info')}
          ${badge(`Impact: ${item.impact_level || 'N/A'}`, badgeTone(item.impact_level))}
          ${badge(`Effort: ${item.effort_level || 'N/A'}`, badgeTone(item.effort_level))}
          ${badge(item.time_horizon || 'N/A', badgeTone(item.time_horizon))}
        </div>
      </div>
    </div>
    <div class="aes-grid two">
      <div class="aes-panel">
        <div class="aes-label">Why now</div>
        <div class="aes-text">${esc(item.why_now)}</div>
      </div>
      <div class="aes-panel">
        <div class="aes-label">Business outcome</div>
        <div class="aes-text">${esc(item.business_outcome)}</div>
      </div>
      <div class="aes-panel">
        <div class="aes-label">Scope</div>
        <div class="aes-text">${esc(item.scope)}</div>
      </div>
      <div class="aes-panel">
        <div class="aes-label owner">Owner</div>
        <div class="aes-text">${esc(item.owner)}</div>
      </div>
    </div>
    <div class="aes-grid four top-border">
      <div class="aes-panel">
        <div class="aes-label">Dependencies</div>
        ${deps || '<div class="aes-text subtle">None called out.</div>'}
      </div>
      <div class="aes-panel">
        <div class="aes-label risk">Risks</div>
        ${risks || '<div class="aes-text subtle">No major risks called out.</div>'}
      </div>
      <div class="aes-panel">
        <div class="aes-label success">Success measures</div>
        ${measures || '<div class="aes-text subtle">No success measures provided.</div>'}
      </div>
      <div class="aes-panel">
        <div class="aes-label evidence">Evidence</div>
        ${evidence || '<div class="aes-text subtle">No evidence captured.</div>'}
      </div>
    </div>
  </div>`;
};

const phaseCard = (phase = {}) => `
  <div class="aes-phase">
    <div class="aes-phase-header">
      <div class="aes-phase-circle">${esc(phase.phase ?? '')}</div>
      <div class="aes-phase-title-wrap">
        <div class="aes-phase-title">${esc(phase.title)}</div>
        <div class="aes-phase-objective">${esc(phase.objective)}</div>
      </div>
    </div>
    <div class="aes-grid two phase-body">
      <div class="aes-panel">
        <div class="aes-label">Why this phase</div>
        <div class="aes-text">${esc(phase.why_this_phase)}</div>
      </div>
      <div class="aes-panel">
        <div class="aes-label">Included priorities</div>
        <div class="aes-chip-row">${arr(phase.included_priorities).map((n) => `<span class="aes-chip">Priority ${esc(n)}</span>`).join('')}</div>
      </div>
      <div class="aes-panel">
        <div class="aes-label">Entry criteria</div>
        ${renderList(phase.entry_criteria, 'aes-subitem')}
      </div>
      <div class="aes-panel">
        <div class="aes-label success">Exit criteria</div>
        ${renderList(phase.exit_criteria, 'aes-subitem')}
      </div>
    </div>
    <div class="aes-panel top-border">
      <div class="aes-label benefit">Expected outcomes</div>
      <div class="aes-outcome-row">${arr(phase.expected_outcomes).map((o) => `<span class="aes-outcome-pill">${esc(o)}</span>`).join('')}</div>
    </div>
  </div>`;

const principleCard = (item = {}) => `
  <div class="aes-mini-card">
    <div class="aes-mini-title">${esc(item.title)}</div>
    <div class="aes-text">${esc(item.detail)}</div>
  </div>`;

const notYetCard = (item = {}) => `
  <div class="aes-notyet-card">
    <div class="aes-mini-title">${esc(item.item)}</div>
    <div class="aes-text">${esc(item.reason)}</div>
  </div>`;

const CSS = `
  html,body{margin:0;padding:0;box-sizing:border-box;}
  *,*::before,*::after{box-sizing:border-box;}
  .aes-doc-header{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:14px 20px;margin-bottom:10px;display:flex;justify-content:space-between;align-items:center;}
  .aes-doc-header-left{display:flex;flex-direction:column;gap:4px;}
  .aes-doc-header-title{font-size:16px;font-weight:700;color:#172B4D;}
  .aes-doc-header-type{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#0052CC;}
  .aes-doc-header-time{font-size:12px;color:#6B778C;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif;background:#F4F5F7;color:#172B4D;}
  .aes-wrap{display:flex;flex-direction:column;gap:10px;padding:10px;}
  .aes-section,.aes-hero,.aes-card,.aes-phase{background:#fff;border:1px solid #E8E9EB;border-radius:10px;overflow:hidden;}
  .aes-section,.aes-hero{padding:20px 22px;}
  .aes-hero{position:relative;}
  .aes-hero::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#0052CC 0%,#0C66E4 60%,#0747A6 100%);}
  .aes-eyebrow{font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#0052CC;margin-bottom:10px;display:flex;align-items:center;gap:6px;}
  .aes-eyebrow::before{content:'';display:inline-block;width:16px;height:2px;background:#0052CC;border-radius:2px;}
  .aes-headline{font-size:22px;font-weight:800;line-height:1.25;color:#172B4D;margin-bottom:12px;}
  .aes-summary{font-size:14px;line-height:1.7;color:#42526E;padding-bottom:16px;border-bottom:1px solid #EBECF0;margin-bottom:14px;}
  .aes-meta{display:flex;flex-wrap:wrap;gap:8px;align-items:center;}
  .aes-badge{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;border:1px solid transparent;}
  .aes-badge.danger{background:#FFEBE6;color:#BF2600;border-color:#FFBDAD;}
  .aes-badge.warning{background:#FFF0B3;color:#974F0C;border-color:#FFE380;}
  .aes-badge.info{background:#E6F1FB;color:#0052CC;border-color:#85B7EB;}
  .aes-badge.neutral{background:#F4F5F7;color:#42526E;border-color:#DFE1E6;}
  .aes-why-now{font-size:13px;line-height:1.65;color:#42526E;margin-top:10px;}
  .aes-section-title{font-size:17px;font-weight:700;color:#172B4D;margin-bottom:14px;display:flex;align-items:center;gap:10px;}
  .aes-section-icon{width:34px;height:34px;border-radius:8px;background:#0052CC;color:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .aes-card{margin-bottom:10px;}
  .aes-card:last-child{margin-bottom:0;}
  .aes-card-bar{display:flex;align-items:stretch;border-bottom:1px solid #EBECF0;}
  .aes-rank-block{min-width:56px;background:#FAFBFC;border-right:1px solid #EBECF0;padding:14px 16px;display:flex;flex-direction:column;align-items:center;justify-content:center;}
  .aes-rank-label{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;}
  .aes-rank-value{font-size:22px;font-weight:800;color:#172B4D;line-height:1;margin-top:2px;}
  .aes-card-title-block{padding:12px 16px;flex:1;}
  .aes-card-title{font-size:16px;font-weight:700;color:#172B4D;margin-bottom:4px;}
  .aes-source-link{font-size:10px;font-weight:700;color:#0C66E4;margin-bottom:7px;}
  .aes-card-badges{display:flex;flex-wrap:wrap;gap:6px;}
  .aes-grid{display:grid;gap:0;}
  .aes-grid.two{grid-template-columns:1fr 1fr;}
  .aes-grid.four{grid-template-columns:1fr 1fr 1fr 1fr;}
  .aes-panel{padding:12px 16px;border-right:1px solid #EBECF0;}
  .aes-grid .aes-panel:last-child{border-right:none;}
  .top-border{border-top:1px solid #EBECF0;}
  .aes-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:6px;}
  .aes-label.owner{color:#974F0C;}
  .aes-label.risk{color:#BF2600;}
  .aes-label.success,.aes-label.benefit{color:#0052CC;}
  .aes-label.evidence{color:#0C66E4;}
  .aes-text{font-size:13px;line-height:1.6;color:#42526E;}
  .aes-text.subtle{color:#6B778C;}
  .aes-subitem,.aes-evidence-item{font-size:12px;line-height:1.55;color:#42526E;padding:3px 0 3px 10px;border-left:2px solid #DFE1E6;margin-bottom:3px;}
  .aes-chip-row,.aes-outcome-row{display:flex;flex-wrap:wrap;gap:6px;}
  .aes-chip{display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;background:#DEEBFF;color:#0747A6;border:1px solid #B3D4FF;font-size:11px;font-weight:700;}
  .aes-outcome-pill{display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#E3FCEF;color:#006644;border:1px solid #79F2C0;font-size:12px;font-weight:700;}
  .aes-phase{margin-bottom:10px;}
  .aes-phase:last-child{margin-bottom:0;}
  .aes-phase-header{display:flex;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid #EBECF0;background:#FAFBFC;}
  .aes-phase-circle{width:36px;height:36px;border-radius:50%;background:#0052CC;color:#fff;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:800;flex-shrink:0;}
  .aes-phase-title{font-size:15px;font-weight:700;color:#172B4D;}
  .aes-phase-objective{font-size:12px;color:#42526E;margin-top:2px;}
  .phase-body .aes-panel:nth-child(2n){border-right:none;}
  .aes-mini-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .aes-mini-card,.aes-notyet-card{background:#FAFBFC;border:1px solid #EBECF0;border-radius:10px;padding:14px;}
  .aes-notyet-card{background:#FFFCF0;border-color:#FFE380;}
  .aes-mini-title{font-size:14px;font-weight:700;color:#172B4D;margin-bottom:6px;}
  @media (max-width:1100px){.aes-grid.four{grid-template-columns:1fr 1fr;}.aes-grid.four .aes-panel:nth-child(2n){border-right:none;}}
  @media (max-width:780px){.aes-grid.two,.aes-grid.four,.aes-mini-grid{grid-template-columns:1fr;}.aes-panel{border-right:none;border-bottom:1px solid #EBECF0;}.aes-grid .aes-panel:last-child{border-bottom:none;}}
`;

export const renderAIExecutionStrategyHTML = (data, modelTitle = '') => {
  const embeddedJson = esc(JSON.stringify(data || null));
  if (!data || typeof data !== 'object') {
    return `<!DOCTYPE html><html><body style="font-family:sans-serif;padding:24px;color:#172B4D">No Execution Strategy data available.<script id="__qa_data__" type="application/json">${embeddedJson}</script></body></html>`;
  }

  const summary = data.summary || {};
  const priorities = arr(data.priority_actions);
  const phases = arr(data.phased_plan);
  const principles = arr(data.sequencing_principles);
  const notYet = arr(data.do_not_do_yet);

  const body = `
    <div class="aes-hero">
      <div class="aes-eyebrow">Execution Strategy</div>
      <div class="aes-headline">${esc(summary.headline || 'Prioritised execution strategy')}</div>
      <div class="aes-summary">${esc(summary.executive_summary || '')}</div>
      <div class="aes-meta">
        ${badge(summary.strategic_position || 'Targeted Improvement', badgeTone(summary.strategic_position))}
        ${badge(`Confidence: ${summary.confidence || 'Medium'}`, 'info')}
      </div>
      <div class="aes-why-now">${esc(summary.why_now || '')}</div>
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">1</span>Priority Actions</div>
      ${priorities.map(priorityCard).join('')}
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">2</span>Phased Plan</div>
      ${phases.map(phaseCard).join('')}
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">3</span>Sequencing Principles</div>
      <div class="aes-mini-grid">${principles.map(principleCard).join('')}</div>
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">4</span>Do Not Do Yet</div>
      <div class="aes-mini-grid">${notYet.map(notYetCard).join('')}</div>
    </div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="renderer-version" content="v2026-08-10-execution-strategy-v3"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Execution Strategy${modelTitle ? ' — ' + esc(modelTitle) : ''}</title>
<style>${CSS}</style>
</head>
<body><div class="aes-wrap">${body}</div><script id="__qa_data__" type="application/json">${embeddedJson}</script></body>
</html>`;
};

export const buildAIExecutionStrategyExportHTML = (data, modelTitle = '') => {
  const embeddedJson = esc(JSON.stringify(data || null));
  if (!data || typeof data !== 'object') {
    return `<!DOCTYPE html><html><body style="font-family:sans-serif;padding:24px;color:#172B4D">No Execution Strategy data available.<script id="__qa_data__" type="application/json">${embeddedJson}</script></body></html>`;
  }

  const summary = data.summary || {};
  const priorities = arr(data.priority_actions);
  const phases = arr(data.phased_plan);
  const principles = arr(data.sequencing_principles);
  const notYet = arr(data.do_not_do_yet);

  const body = `
    ${renderDocHeader(modelTitle)}
    
    <div class="aes-hero">
      <div class="aes-eyebrow">Execution Strategy</div>
      <div class="aes-headline">${esc(summary.headline || 'Prioritised execution strategy')}</div>
      <div class="aes-summary">${esc(summary.executive_summary || '')}</div>
      <div class="aes-meta">
        ${badge(summary.strategic_position || 'Targeted Improvement', badgeTone(summary.strategic_position))}
        ${badge(`Confidence: ${summary.confidence || 'Medium'}`, 'info')}
      </div>
      <div class="aes-why-now">${esc(summary.why_now || '')}</div>
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">1</span>Priority Actions</div>
      ${priorities.map(priorityCard).join('')}
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">2</span>Phased Plan</div>
      ${phases.map(phaseCard).join('')}
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">3</span>Sequencing Principles</div>
      <div class="aes-mini-grid">${principles.map(principleCard).join('')}</div>
    </div>

    <div class="aes-section">
      <div class="aes-section-title"><span class="aes-section-icon">4</span>Do Not Do Yet</div>
      <div class="aes-mini-grid">${notYet.map(notYetCard).join('')}</div>
    </div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="renderer-version" content="v2026-08-10-execution-strategy-v3"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Execution Strategy${modelTitle ? ' — ' + esc(modelTitle) : ''}</title>
<style>${CSS}</style>
</head>
<body><div class="aes-wrap">${body}</div><script id="__qa_data__" type="application/json">${embeddedJson}</script></body>
</html>`;
};

export default renderAIExecutionStrategyHTML;
